import { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const ACCOUNT_TYPE_LABELS = {
  residential: "Residential", business: "Business",
  condo_resident: "Condo Resident", condo_association: "Condo Association",
};

export const PLAN_LABELS = {
  none: "No plan",
  residential_annual: "Residential plan — $249/yr",
  residential_monthly: "Residential plan — $24.99/mo",
  partner_resident: "Partner resident plan — $219/yr",
  commercial: "Commercial plan",
};

const LocationRows = ({ locations, setLocations }) => (
  <div className="sm:col-span-2">
    <p className="text-xs font-bold text-brand-ink mb-1.5">Locations (name + address)</p>
    {locations.map((loc, i) => (
      <div key={loc.id || i} className="flex gap-2 mb-2">
        <Input data-testid={`customer-location-label-${i}`} placeholder="Label (e.g. Home, Store #2)" value={loc.label}
          onChange={(e) => setLocations(locations.map((l, j) => (j === i ? { ...l, label: e.target.value } : l)))}
          className="rounded-sm bg-white w-44" />
        <Input data-testid={`customer-location-address-${i}`} placeholder="Full address" value={loc.address}
          onChange={(e) => setLocations(locations.map((l, j) => (j === i ? { ...l, address: e.target.value } : l)))}
          className="rounded-sm bg-white flex-1" />
        <button type="button" onClick={() => setLocations(locations.filter((_, j) => j !== i))}
          data-testid={`customer-location-remove-${i}`} className="text-brand-red shrink-0" aria-label="Remove location">
          <X className="w-4 h-4" />
        </button>
      </div>
    ))}
    <button type="button" onClick={() => setLocations([...locations, { label: "", address: "" }])}
      data-testid="customer-location-add-btn" className="text-xs font-bold text-brand flex items-center gap-1">
      <Plus className="w-3.5 h-3.5" /> Add location
    </button>
  </div>
);

export const CustomerEditor = ({ initial, onSubmit, submitLabel, busy }) => {
  const [form, setForm] = useState({
    name: initial?.name || "", email: initial?.email || "", phone: initial?.phone || "",
    account_type: initial?.account_type || "residential",
    partner_building: initial?.partner_building || "",
    plan_status: initial?.plan_status || "none",
    plan_start: initial?.plan_start || "", plan_renewal: initial?.plan_renewal || "",
  });
  const [locations, setLocations] = useState(initial?.locations || []);
  const isEdit = Boolean(initial?.id);
  const set = (k) => (v) => setForm({ ...form, [k]: v });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...form, locations }); }}
      className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-brand-subtle border border-zinc-200 rounded-sm p-4">
      <Input data-testid="customer-name-input" required placeholder="Full name / business name" value={form.name}
        onChange={(e) => set("name")(e.target.value)} className="rounded-sm bg-white" />
      <Input data-testid="customer-email-input" required type="email" placeholder="Email" value={form.email} disabled={isEdit}
        onChange={(e) => set("email")(e.target.value)} className="rounded-sm bg-white disabled:opacity-60" />
      <Input data-testid="customer-phone-input" placeholder="Phone" value={form.phone}
        onChange={(e) => set("phone")(e.target.value)} className="rounded-sm bg-white" />
      <Select value={form.account_type} onValueChange={set("account_type")}>
        <SelectTrigger data-testid="customer-type-select" className="rounded-sm bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(ACCOUNT_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
      {form.account_type === "condo_resident" && (
        <Input data-testid="customer-building-input" placeholder="Partner building name" value={form.partner_building}
          onChange={(e) => set("partner_building")(e.target.value)} className="rounded-sm bg-white sm:col-span-2" />
      )}
      <Select value={form.plan_status} onValueChange={set("plan_status")}>
        <SelectTrigger data-testid="customer-plan-select" className="rounded-sm bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(PLAN_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex gap-2">
        <div className="flex-1">
          <p className="text-[10px] uppercase font-bold text-zinc-500 mb-0.5">Plan start</p>
          <Input data-testid="customer-plan-start-input" type="date" value={form.plan_start}
            onChange={(e) => set("plan_start")(e.target.value)} className="rounded-sm bg-white" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase font-bold text-zinc-500 mb-0.5">Renewal</p>
          <Input data-testid="customer-plan-renewal-input" type="date" value={form.plan_renewal}
            onChange={(e) => set("plan_renewal")(e.target.value)} className="rounded-sm bg-white" />
        </div>
      </div>
      <LocationRows locations={locations} setLocations={setLocations} />
      <button type="submit" disabled={busy} data-testid="customer-editor-submit-btn"
        className="sm:col-span-2 bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-sm text-sm flex items-center justify-center gap-2 transition-colors duration-200">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {submitLabel}
      </button>
    </form>
  );
};
